// Marketplace Phase 1 — DemandSummaryHero (Feb 2026).
//
// One-glance summary of the latest snapshot:
//   • "X players in cohort (Y% have wishlist data)"
//   • last snapshot timestamp (UTC + admin local tz)
//   • a clear "insufficient data" warning when total_users_included < 50
//
// No user IDs. No PII. Read-only.

import React from "react";
import { AlertTriangle, Database, Clock } from "lucide-react";

const fmtPct = (n, d) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : "0.0%");

const fmtUtcAndLocal = (iso) => {
  if (!iso) return { utc: "—", local: "—" };
  try {
    const d = new Date(iso);
    return {
      utc: d.toISOString().replace("T", " ").slice(0, 19) + " UTC",
      local: d.toLocaleString(undefined, { hour12: false }),
    };
  } catch {
    return { utc: iso, local: iso };
  }
};

export const DemandSummaryHero = ({ snapshot, t }) => {
  if (!snapshot) {
    return (
      <div
        className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-slate-600"
        data-testid="demand-hero-empty"
      >
        <div className="flex items-center gap-2 text-sm">
          <Database className="h-4 w-4" />
          {t("marketplace.demand.noSnapshot")}
        </div>
      </div>
    );
  }
  const ts = fmtUtcAndLocal(snapshot.generated_at);
  const included = snapshot.total_users_included ?? 0;
  const total = snapshot.total_users_in_platform ?? 0;
  const insufficient = included < 50;
  return (
    <div
      className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 sm:p-6"
      data-testid="demand-hero"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-emerald-700 font-medium">
            {t("marketplace.demand.heroLabel")}
          </div>
          <div className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900">
            <span data-testid="demand-hero-included">{included.toLocaleString()}</span>
            <span className="text-slate-400 text-base ms-2 font-normal">
              / {total.toLocaleString()} {t("marketplace.demand.platformUsers")}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {t("marketplace.demand.coverage")}: <span className="font-mono">{fmtPct(included, total)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            <span className="font-mono" data-testid="demand-hero-utc">{ts.utc}</span>
          </div>
          <div className="font-mono" data-testid="demand-hero-local">{ts.local}</div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400" data-testid="demand-hero-id">
            {snapshot.id}
          </div>
        </div>
      </div>
      {insufficient && (
        <div
          className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
          data-testid="demand-hero-insufficient-warning"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{t("marketplace.demand.insufficientData")}</span>
        </div>
      )}
    </div>
  );
};

export default DemandSummaryHero;
